import { View, Input } from "@tarojs/components";
import React, {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { inputRange } from "@/utils";
import classNames from "classnames";
import ActionSheet from "../ActionSheet";

import "./index.scss";

export type AddLinkManProps = {
  linkManNum: number;
  onChange?: (data: LinkManItem[], pass: boolean) => void;
  onGetContacts?: (index: number) => void;
  showImportIcon?: boolean;
  onBlur?: (type: keyof LinkManItem, index: number) => void;
  onCheckRelation?: (
    value: { text: string; value: string },
    index: number
  ) => void;
  onInputChange?: (type: "linkmanName" | "linkmanPhone", index: number) => void;
  /**
   * 可以选择的关系
   */
  relations?: { text: string; value: string }[];
  needName?: boolean;
  dataTrackId?: string;
};

const LinkManTitleMap = [
  "一",
  "二",
  "三",
  "四",
  "五",
  "六",
  "七",
  "八",
  "九",
  "十",
];

/**
 * 错误信息提示
 */
const errorMessageMap = {
  linkmanName: "请输入联系人姓名",
  linkmanPhone: "请输入正确的手机号",
  relationType: "请选择与联系人关系",
};

const defaultActionSheet = [
  {
    value: "FATHER",
    text: "父亲",
  },
  {
    value: "MOTHER",
    text: "母亲",
  },
  {
    value: "MATE",
    text: "配偶",
  },
  {
    value: "CHILDREN",
    text: "子女",
  },
  {
    value: "SIBLING",
    text: "兄弟姐妹",
  },
  {
    value: "FRIENDS",
    text: "朋友",
  },
  {
    value: "COLLEAGUE",
    text: "同事",
  },
  {
    value: "RELATIVES",
    text: "亲戚",
  },
  {
    value: "OTHER",
    text: "其他",
  },
];

export type LinkManItem = {
  linkmanName: string;
  linkmanPhone: string;
  relationType: string;
  relationValue: string;
};

export type LinkManRef = {
  canSubmit: boolean;
  setContacts: (
    item: Partial<Pick<LinkManItem, "linkmanName" | "linkmanPhone">>,
    index: number
  ) => void;
};

const defaultShowImportIcon =
  process.env.TARO_ENV === "h5"
    ? window.navigator.userAgent.includes("xhqb")
    : true;

const XHAddLinMan = forwardRef<LinkManRef, AddLinkManProps>(
  (props, fromRef) => {
    const {
      linkManNum,
      relations = defaultActionSheet,
      onChange,
      onGetContacts,
      showImportIcon = defaultShowImportIcon,
      onBlur,
      onCheckRelation,
      onInputChange,
      needName = true,
      dataTrackId = "XHAddLinMan",
    } = props;

    // form信息
    const [formData, setFormData] = useState<LinkManItem[]>([]);

    // 错误提示语信息
    const [errMessage, setErrMessage] = useState<
      Omit<LinkManItem, "relationValue">[]
    >([]);

    useEffect(() => {
      setFormData(
        Array(linkManNum)
          .fill(1)
          .map(() => {
            return {
              linkmanName: "",
              linkmanPhone: "",
              relationType: "",
              relationValue: "",
            };
          })
      );
      setErrMessage(
        Array(linkManNum)
          .fill(1)
          .map(() => {
            return {
              linkmanName: "",
              linkmanPhone: "",
              relationType: "",
            };
          })
      );
    }, [linkManNum]);

    /**
     * 校验手机号
     */
    const validatePhone = useCallback((phone: string) => {
      if (/1\d{10}/.test(phone)) {
        return true;
      }
      return false;
    }, []);

    /**
     * 非空校验
     */
    const validateForm = useCallback(
      (type: keyof LinkManItem, name: string) => {
        if (type === "linkmanName") {
          if (!name || !name.replace(/^\s+|\s+$/gm, "")) return false;
          if (name.length > 20) return false;
          return true;
        } else if (type === "linkmanPhone") {
          if (!name || !validatePhone(name)) return false;
          return true;
        } else if (type === "relationType") {
          if (!name) return false;
          return true;
        }
        return false;
      },
      []
    );

    /**
     * 校验联系人是否合法，默认找到不合法的就退出循环
     */
    const validateCanSubmit = useMemo(() => {
      const finalData = formData;
      const length = finalData.length;
      const map: Record<string, string[]> = {
        linkmanName: [],
        linkmanPhone: [],
        relationValue: [],
      };
      let flag = true;

      /**
       * 每次循环formData，不符合直接break，退出循环
       */
      for (let index = 0; index < length; index++) {
        if (needName) {
          const isNameValid = validateForm(
            "linkmanName",
            finalData[index].linkmanName
          );
          if (!isNameValid) {
            flag = false;
            break;
          }
        }

        const isPhoneValid = validateForm(
          "linkmanPhone",
          finalData[index].linkmanPhone
        );
        if (!isPhoneValid) {
          flag = false;
          break;
        }
        if (
          index !== 0 &&
          map.linkmanPhone.includes(finalData[index].linkmanPhone)
        ) {
          flag = false;
          break;
        }
        map.linkmanPhone.push(finalData[index].linkmanPhone);
        const isRelationValid = validateForm(
          "relationType",
          finalData[index].relationValue
        );
        if (!isRelationValid) {
          flag = false;
          break;
        }
        map.relationValue.push(finalData[index].relationValue);
      }
      if (map.relationValue.filter(item => item === "MATE").length >= 2) {
        flag = false;
      }

      return flag;
    }, [formData, needName]);

    useEffect(() => {
      onChange?.(formData, validateCanSubmit);
    }, [formData, validateCanSubmit]);

    useImperativeHandle(
      fromRef,
      () => ({
        canSubmit: validateCanSubmit,
        setContacts(item, index) {
          setFormData(old => {
            old[index].linkmanName = item.linkmanName || "";
            old[index].linkmanPhone = item.linkmanPhone || "";
            return [...old];
          });
          onFormBlur("linkmanName", index);
          onFormBlur("linkmanPhone", index);
        },
      }),
      [validateCanSubmit]
    );

    /**
     * 重复校验
     * @param type 校验的字段类型
     * @param currentIndex 当前校验的索引
     * @param customFormData 可选的自定义数据，如果不传则使用 formData
     */
    const validateDuplicate = (
      type: keyof LinkManItem,
      currentIndex,
      customFormData?: LinkManItem[]
    ): { pass: boolean; message: string } => {
      const dataToValidate = customFormData || formData;
      const currentItem = dataToValidate[currentIndex];
      // const validStub: Record<
      //   Exclude<keyof LinkManItem, "relationType">,
      //   string[]
      // > = {
      //   linkmanName: [],
      //   linkmanPhone: [],
      //   relationValue: [],
      // };

      for (
        let index = 0, length = dataToValidate.length;
        index < length;
        index++
      ) {
        if (index === currentIndex) continue; // 不能跟自己比较
        const current = dataToValidate[index];
        if (type === "linkmanPhone") {
          if (
            currentItem.linkmanPhone !== "" &&
            current.linkmanPhone !== "" &&
            current.linkmanPhone === currentItem.linkmanPhone
          ) {
            // 如果没有重复需要清空错误信息
            return {
              pass: false,
              message: `手机号与第${index + 1}联系人号码重复，请修改`,
            };
          } else {
            // setErrMessage(old => {
            //   old[index].linkmanPhone = "";
            //   old[currentIndex].linkmanPhone = "";
            //   return [...old];
            // });
          }
        } else if (type === "relationValue") {
          if (
            currentItem.relationValue !== "" &&
            current.relationValue === "MATE" &&
            currentItem.relationValue === "MATE"
          ) {
            return {
              pass: false,
              message: `关系与第${index + 1}联系人关系重复，请修改`,
            };
          } else {
            // setErrMessage(old => {
            //   old[index].relationType = "";
            //   old[currentIndex].relationType = "";
            //   return [...old];
            // });
          }
        }
      }
      return { pass: true, message: "" };
    };

    /**
     * 联系人姓名改变
     */
    const onNameChange = useCallback((evt, index) => {
      const value =
        process.env.TARO_ENV === "h5" ? evt.target.value : evt.detail.value;
      const name = inputRange(value);
      setFormData(old => {
        old[index].linkmanName = name;
        return [...old];
      });
      onInputChange?.("linkmanName", index);
    }, []);

    /**
     * 联系人手机号改变
     */
    const onPhoneChange = useCallback((evt, index) => {
      const value =
        process.env.TARO_ENV === "h5" ? evt.target.value : evt.detail.value;
      const phone = inputRange(value);
      setFormData(old => {
        old[index].linkmanPhone = phone;
        return [...old];
      });
      onInputChange?.("linkmanPhone", index);
    }, []);

    /**
     * 输入框失去焦点做校验
     */
    const onFormBlur = useCallback(
      (type: keyof LinkManItem, index: number) => {
        const flag = validateForm(type, formData[index][type]); // 先做值的校验，再做重复校验
        if (!flag) {
          setErrMessage(old => {
            old[index][type] = errorMessageMap[type];
            return [...old];
          });
          return;
        }
        const { pass, message } = validateDuplicate(type, index);
        if (!pass) {
          setErrMessage(old => {
            old[index][type] = message;
            return [...old];
          });
        }
        onBlur?.(type, index);
      },
      [formData]
    );

    /**
     * 聚焦的时候设置错误信息为空
     */
    const onFormFocus = useCallback(
      (type: keyof LinkManItem, index: number) => {
        setErrMessage(old => {
          old[index][type] = "";
          return [...old];
        });
      },
      []
    );

    return (
      <View
        className="xh-linkman"
        // @ts-ignore
        dataTrackId={dataTrackId}
      >
        {formData.map((item, num) => (
          <View key={num} className="xh-linkman-item">
            <View className="xh-linkman-title">
              第{LinkManTitleMap[num]}联系人
            </View>
            {needName && (
              <View className="xh-linkman-main">
                <View className="xh-linkman-main-input">
                  <View className="xh-linkman-main-input-label">姓名</View>
                  <View className="xh-linkman-main-input-main">
                    {process.env.TARO_ENV === "h5" ? (
                      <input
                        data-track-id={`${dataTrackId}_${num}-name`}
                        className="xh-linkman-main-input-main-input"
                        type="text"
                        placeholder="紧急联系人姓名"
                        onChange={e => onNameChange(e, num)}
                        value={item.linkmanName}
                        onBlur={() => onFormBlur("linkmanName", num)}
                        onFocus={() => onFormFocus("linkmanName", num)}
                      />
                    ) : (
                      <Input
                        // @ts-ignore
                        dataTrackId={`${dataTrackId}_${num}-name`}
                        placeholder="紧急联系人姓名"
                        className="xh-linkman-main-input-main-input"
                        onBlur={() => onFormBlur("linkmanName", num)}
                        value={item.linkmanName}
                        type="text"
                        onInput={e => onNameChange(e, num)}
                        onFocus={() => onFormFocus("linkmanName", num)}
                      />
                    )}
                    {errMessage[num].linkmanName && (
                      <View className="xh-linkman-main-input-main-err">
                        {errMessage[num].linkmanName}
                      </View>
                    )}
                  </View>
                  {showImportIcon && (
                    <View
                      // @ts-ignore
                      dataTrackId={`${dataTrackId}_${num}-name-contacts`}
                      className="xh-linkman-main-input-contacts"
                      onClick={() => onGetContacts?.(num)}
                    ></View>
                  )}
                </View>
              </View>
            )}
            <View className="xh-linkman-main">
              <View className="xh-linkman-main-input">
                <View className="xh-linkman-main-input-label">电话</View>
                <View className="xh-linkman-main-input-main">
                  {process.env.TARO_ENV === "h5" ? (
                    <input
                      data-track-id={`${dataTrackId}_${num}-phone`}
                      className="xh-linkman-main-input-main-input"
                      type="tel"
                      placeholder="紧急联系人手机号"
                      onBlur={() => onFormBlur("linkmanPhone", num)}
                      maxLength={11}
                      onChange={e => onPhoneChange(e, num)}
                      value={item.linkmanPhone}
                      onFocus={() => onFormFocus("linkmanPhone", num)}
                    />
                  ) : (
                    <Input
                      // @ts-ignore
                      dataTrackId={`${dataTrackId}_${num}-phone`}
                      placeholder="紧急联系人手机号"
                      className="xh-linkman-main-input-main-input"
                      type="number"
                      maxlength={11}
                      onBlur={() => onFormBlur("linkmanPhone", num)}
                      value={item.linkmanPhone}
                      onInput={e => onPhoneChange(e, num)}
                      onFocus={() => onFormFocus("linkmanPhone", num)}
                    />
                  )}
                  {errMessage[num].linkmanPhone && (
                    <View className="xh-linkman-main-input-main-err">
                      {errMessage[num].linkmanPhone}
                    </View>
                  )}
                </View>
                {showImportIcon && !needName && (
                  <View
                    // @ts-ignore
                    dataTrackId={`${dataTrackId}_${num}-phone-contacts`}
                    className="xh-linkman-main-input-contacts"
                    onClick={() => onGetContacts?.(num)}
                  ></View>
                )}
              </View>
            </View>
            <ActionSheet
              data={relations}
              title="选择与联系人的关系"
              // @ts-ignore
              dataTrackId={`${dataTrackId}_${num}-relation`}
              onConfirm={value => {
                // 先清空当前项的错误信息
                setErrMessage(old => {
                  const newErr = [...old];
                  newErr[num].relationType = "";
                  return newErr;
                });

                // 使用 setFormData 的回调形式，直接基于新数据进行校验
                setFormData(old => {
                  const newData = [...old];
                  newData[num] = {
                    ...newData[num],
                    relationType: value.text,
                    relationValue: value.value,
                  };

                  // 直接基于新数据进行重复校验
                  const { pass, message } = validateDuplicate(
                    "relationValue",
                    num,
                    newData
                  );

                  if (!pass) {
                    // 需要在下一次状态更新时设置错误信息
                    setTimeout(() => {
                      setErrMessage(errOld => {
                        const newErr = [...errOld];
                        newErr[num].relationType = message;
                        return newErr;
                      });
                    }, 0);
                  }

                  return newData;
                });

                onCheckRelation?.(value, num);
              }}
              onCancel={() => onFormBlur("relationType", num)}
            >
              <View className="xh-linkman-main">
                <View className="xh-linkman-main-input">
                  <View className="xh-linkman-main-input-label">关系</View>
                  <View className="xh-linkman-main-input-main">
                    <View
                      // @ts-ignore
                      dataTrackId={`${dataTrackId}_${num}-relation-input`}
                      className={classNames(
                        "xh-linkman-main-input-main-input",
                        {
                          placeholder: !item.relationValue,
                          tt: process.env.TARO_ENV === "tt",
                        }
                      )}
                    >
                      {item.relationType || "选择与联系人关系"}
                    </View>
                    {errMessage[num].relationType && (
                      <View className="xh-linkman-main-input-main-err">
                        {errMessage[num].relationType}
                      </View>
                    )}
                  </View>
                  <View className="xh-linkman-main-input-arrow"></View>
                </View>
              </View>
            </ActionSheet>
          </View>
        ))}
      </View>
    );
  }
);

export default XHAddLinMan;
